"use client";

import { useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useT } from "@/hooks/useT";

interface Props {
  /** YES + NO Polymarket CLOB token IDs. Index 0 = YES, 1 = NO. Accepts
   *  the array directly or the JSON-stringified form persisted by the
   *  older DegenBox modal. */
  clobTokenIds: string[] | string | null | undefined;
  /** Unique id used for SVG gradient `<defs>` keys — must be different
   *  per chart instance on the page or gradients collide. */
  id: string;
  /** Lookback window the prices-history endpoint understands. */
  interval?: "1d" | "1w" | "1m" | "all";
  /** Sample fidelity in minutes. */
  fidelity?: number;
}

/**
 * Dual YES/NO area chart for a binary Polymarket market. Fetches both
 * outcome histories in parallel, merges them by timestamp and renders
 * an AreaChart with two filled gradients.
 *
 * Used in DegenBox's binary MarketTile (chart view) and in each
 * expanded outcome row of the multi-outcome EventTile.
 */
export default function MiniDualChart({
  clobTokenIds,
  id,
  interval = "1w",
  fidelity = 60,
}: Props) {
  const t = useT();
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Normalise the tokenIds input — it can arrive as a real array or the
  // JSON-stringified form persisted by older modal snapshots.
  const tokenIds: string[] | null = (() => {
    const raw = clobTokenIds;
    if (Array.isArray(raw)) return raw.filter((x) => typeof x === "string") as string[];
    if (typeof raw === "string") {
      try {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return arr.filter((x) => typeof x === "string") as string[];
      } catch { /* fall through */ }
      if (raw.includes(",")) return raw.split(",").map((s) => s.trim()).filter(Boolean);
    }
    return null;
  })();

  useEffect(() => {
    let cancelled = false;
    async function fetchPriceHistory() {
      if (!tokenIds || tokenIds.length < 2) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const [yesResponse, noResponse] = await Promise.all([
          fetch(`/api/polymarket/prices-history?tokenId=${tokenIds[0]}&interval=${interval}&fidelity=${fidelity}`),
          fetch(`/api/polymarket/prices-history?tokenId=${tokenIds[1]}&interval=${interval}&fidelity=${fidelity}`),
        ]);
        const [yesResult, noResult] = await Promise.all([yesResponse.json(), noResponse.json()]);

        const yesHistory = yesResult.success ? yesResult.data.history : [];
        const noHistory = noResult.success ? noResult.data.history : [];

        const priceMap = new Map<number, { yesPrice: number | null; noPrice: number | null }>();
        yesHistory.forEach((point: { time: number; price: number }) => {
          priceMap.set(point.time, { yesPrice: point.price * 100, noPrice: null });
        });
        noHistory.forEach((point: { time: number; price: number }) => {
          const existing = priceMap.get(point.time);
          if (existing) existing.noPrice = point.price * 100;
          else priceMap.set(point.time, { yesPrice: null, noPrice: point.price * 100 });
        });

        const dataPoints = Array.from(priceMap.entries())
          .map(([timestamp, prices]) => ({
            timestamp: timestamp * 1000,
            yesPrice: prices.yesPrice,
            noPrice: prices.noPrice,
          }))
          .sort((a, b) => a.timestamp - b.timestamp);

        if (!cancelled) setChartData(dataPoints);
      } catch {
        if (!cancelled) setChartData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchPriceHistory();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tokenIds), interval, fidelity]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!tokenIds || tokenIds.length < 2) {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <p className="text-xs text-gray-400 text-center">
          {t("degen.tile.chartUnavailable")}
        </p>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <p className="text-xs text-gray-400 text-center">
          {t("degen.tile.noTradingData")}
        </p>
      </div>
    );
  }

  return (
    <div className="h-full p-2">
      <ResponsiveContainer width="100%" height="100%" minHeight={1}>
        <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id={`yesGradient-${id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
            </linearGradient>
            <linearGradient id={`noGradient-${id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#f87171" stopOpacity={0.2} />
              <stop offset="100%" stopColor="#f87171" stopOpacity={0} />
            </linearGradient>
          </defs>

          <XAxis dataKey="timestamp" hide />
          <YAxis domain={[0, 100]} hide />

          <ReferenceLine y={50} stroke="#4B5563" strokeDasharray="3 3" strokeOpacity={0.3} />

          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const yesPrice = payload.find(p => p.dataKey === 'yesPrice')?.value as number;
                const noPrice = payload.find(p => p.dataKey === 'noPrice')?.value as number;
                return (
                  <div className="bg-gray-900 dark:bg-[#18181D] px-2.5 py-1.5 rounded-[5px] shadow-xl border border-gray-700 dark:border-white/[0.1] text-xs">
                    {yesPrice != null && (
                      <div className="flex items-center gap-2">
                        <span className="text-emerald-400 font-semibold">YES</span>
                        <span className="text-white tabular-nums">{yesPrice.toFixed(0)}%</span>
                      </div>
                    )}
                    {noPrice != null && (
                      <div className="flex items-center gap-2">
                        <span className="text-red-400 font-semibold">NO</span>
                        <span className="text-white tabular-nums">{noPrice.toFixed(0)}%</span>
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            }}
          />

          <Area
            type="monotone"
            dataKey="yesPrice"
            stroke="#10b981"
            strokeWidth={2}
            fill={`url(#yesGradient-${id})`}
            connectNulls
            dot={false}
          />
          <Area
            type="monotone"
            dataKey="noPrice"
            stroke="#f87171"
            strokeWidth={2}
            fill={`url(#noGradient-${id})`}
            connectNulls
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
